import type { MouseEvent } from "react";
import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import { GLOBAL_SEARCH_TAB_LABEL_KEYS } from "./constants";
import type { IGlobalSearchTabButtonProps } from "./types";

// NOTE: NSAI-6853 — one category chip. The search box keeps the focus when a
// chip is clicked (mousedown is cancelled), so the user can go on typing and
// ↑/↓/↵ keep driving the results.
const GlobalSearchTabButtonComponent = ({
  definition,
  isActive,
  onSelectTab,
}: IGlobalSearchTabButtonProps): JSX.Element => {
  const { t } = useTranslation("globalSearch");

  const label = t(GLOBAL_SEARCH_TAB_LABEL_KEYS[definition.tab]);

  const handleMouseDown = useCallback((event: MouseEvent): void => {
    event.preventDefault();
  }, []);

  const handleClick = useCallback((): void => {
    onSelectTab(definition.tab);
  }, [definition.tab, onSelectTab]);

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      tabIndex={isActive ? 0 : -1}
      data-state={isActive ? "active" : "inactive"}
      data-testid={`global-search-tab-${definition.tab}`}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      title={label}
      className={cn(
        // NOTE: Capped, so one long translation ("Настройки организации",
        // "ملفات الذكاء الاصطناعي") never turns into a chip wider than the strip.
        "h-8 max-w-48 shrink-0 truncate whitespace-nowrap rounded-full px-3.5 text-sm transition-[background-color,color,box-shadow] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none",
        isActive
          ? "bg-glass-fill-strong font-medium text-foreground shadow-[inset_0_0_0_1px_var(--glass-edge)]"
          : "text-foreground-secondary hover:bg-glass-fill hover:text-foreground",
      )}
    >
      {label}
    </button>
  );
};

export const GlobalSearchTabButton = memo(GlobalSearchTabButtonComponent);
